import { hasAnalyticsConsent, hasMarketingConsent } from "@common/services/consent/store";

/**
 * Attribution tracking utilities for BlueTicks
 *
 * Multi-Touch Attribution Strategy:
 * - first_touch: Preserves original source for internal analytics
 * - conversion_ids: Stores ALL click IDs for ad platform conversion reporting
 *
 * When user converts, we report ALL click IDs to respective platforms:
 * - Google Ads gets all gclids
 * - Facebook Ads gets all fbclids
 * - Microsoft Ads gets all msclkids
 * - TikTok Ads gets all ttclids
 *
 * Each platform handles attribution logic (windows, models) on their end.
 */

interface AttributionData {
  // First-touch attribution for internal analytics
  first_touch: {
    utm_source?: string | null;
    utm_medium?: string | null;
    utm_campaign?: string | null;
    utm_content?: string | null;
    utm_term?: string | null;
    landing_page?: string | null;
    timestamp: string;
    page_url: string;
    referrer: string;
  };

  // Multi-touch conversion tracking - ALL unique click IDs
  conversion_ids: {
    gclids: string[];   // All Google Ads click IDs
    fbclids: string[];  // All Facebook Ads click IDs
    msclkids: string[]; // All Microsoft Ads click IDs
    ttclids: string[];  // All TikTok Ads click IDs
  };

  // Google Analytics Client ID
  ga_client_id?: string | null;

  // Google Analytics Session ID (for MP event session matching)
  ga_session_id?: string | null;

  // Stable visitor ID (browser fingerprint) for cross-platform tracking
  stable_visitor_id?: string;

  // Last update timestamp
  last_updated: string;
}

/**
 * Extract Google Analytics Session ID from _ga_RCEGPEQEZ5 cookie
 * Format: GS2.2.s{sessionId}$o1$g1$t...
 */
export function getGaSessionId(): string | null {
  try {
    if (typeof document === 'undefined') return null;

    const cookieVal = document.cookie
      .split('; ')
      .find(row => row.startsWith('_ga_RCEGPEQEZ5='));

    if (!cookieVal) return null;

    const match = cookieVal.match(/\.s(\d+)\$/);
    return match ? match[1] : null;
  } catch (error) {
    console.warn('Error extracting GA Session ID:', error);
    return null;
  }
}

/**
 * Extract Google Analytics Client ID from _ga cookie
 * Supports both GA4 and Universal Analytics formats
 */
export function getGaClientId(): string | null {
  try {
    // Check if we're in browser environment
    if (typeof document === 'undefined') return null;

    const cookieVal = document.cookie
      .split('; ')
      .find(row => row.startsWith('_ga='));
    
    if (!cookieVal) return null;

    const parts = cookieVal.split('.');
    if (parts.length >= 4) {
      // Classic GA client ID format: GA1.1.client_id_part1.client_id_part2
      return `${parts[2]}.${parts[3]}`;
    }
    
    return null;
  } catch (error) {
    console.warn('Error extracting GA Client ID:', error);
    return null;
  }
}

/**
 * True only for GA's native client id shape `<rand>.<timestamp>` (digits.digits).
 * Anything else (e.g. the 64-hex stable-visitor hash) can never join a GA4 user.
 */
export function isValidGaClientId(value: unknown): value is string {
  return typeof value === 'string' && /^\d{5,}\.\d{5,}$/.test(value);
}

/**
 * Get UTM parameters from current URL or sessionStorage (survives redirects)
 */
export function getUtmParameters(): {
  utm_source?: string | null;
  utm_medium?: string | null;
  utm_campaign?: string | null;
  utm_content?: string | null;
  utm_term?: string | null;
} {
  try {
    if (typeof window === 'undefined') {
      return {
        utm_source: null,
        utm_medium: null,
        utm_campaign: null,
        utm_content: null,
        utm_term: null,
      };
    }

    // First check current URL
    const params = new URLSearchParams(window.location.search);
    const currentUtm = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_content: params.get('utm_content'),
      utm_term: params.get('utm_term'),
    };

    // If current URL has UTM params, store them in sessionStorage for redirect survival
    const hasUtmInUrl = Object.values(currentUtm).some(v => v !== null);
    if (hasUtmInUrl && typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem('blueticks_utm_params', JSON.stringify(currentUtm));
      return currentUtm;
    }

    // If no UTM in URL, check sessionStorage (might have been stored before redirect)
    if (typeof sessionStorage !== 'undefined') {
      const storedUtm = sessionStorage.getItem('blueticks_utm_params');
      if (storedUtm) {
        return JSON.parse(storedUtm);
      }
    }

    return currentUtm;
  } catch (error) {
    console.warn('Error extracting UTM parameters:', error);
    return {
      utm_source: null,
      utm_medium: null,
      utm_campaign: null,
      utm_content: null,
      utm_term: null,
    };
  }
}

/**
 * Get click IDs from URL parameters or sessionStorage (survives redirects)
 * Extracts gclid (Google), fbclid (Facebook), msclkid (Microsoft), ttclid (TikTok)
 */
export function getClickIds(): {
  gclid?: string | null;
  fbclid?: string | null;
  msclkid?: string | null;
  ttclid?: string | null;
} {
  try {
    if (typeof window === 'undefined') {
      return {
        gclid: null,
        fbclid: null,
        msclkid: null,
        ttclid: null,
      };
    }

    // First check current URL
    const params = new URLSearchParams(window.location.search);
    const currentClickIds = {
      gclid: params.get('gclid'),
      fbclid: params.get('fbclid'),
      msclkid: params.get('msclkid'),
      ttclid: params.get('ttclid'),
    };

    // If current URL has click IDs, store them in sessionStorage for redirect survival
    const hasClickIdsInUrl = Object.values(currentClickIds).some(v => v !== null);
    if (hasClickIdsInUrl && typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem('blueticks_click_ids', JSON.stringify(currentClickIds));
      return currentClickIds;
    }

    // If no click IDs in URL, check sessionStorage (might have been stored before redirect)
    if (typeof sessionStorage !== 'undefined') {
      const storedClickIds = sessionStorage.getItem('blueticks_click_ids');
      if (storedClickIds) {
        return JSON.parse(storedClickIds);
      }
    }

    return currentClickIds;
  } catch (error) {
    console.warn('Error extracting click IDs:', error);
    return {
      gclid: null,
      fbclid: null,
      msclkid: null,
      ttclid: null,
    };
  }
}

/**
 * Get landing page URL (first page in session)
 * Uses sessionStorage to persist across navigation within the same session
 */
export function getLandingPage(): string {
  try {
    if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
      return '';
    }

    // Check if landing page is already stored in this session
    const storedLandingPage = sessionStorage.getItem('blueticks_landing_page');
    if (storedLandingPage) {
      return storedLandingPage;
    }

    // First page of the session - store it
    const landingPage = window.location.href;
    sessionStorage.setItem('blueticks_landing_page', landingPage);
    return landingPage;
  } catch (error) {
    console.warn('Error tracking landing page:', error);
    return '';
  }
}

/**
 * Detect if we're running in development (localhost)
 */
function isLocalhost(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.hostname === 'localhost' || 
         window.location.hostname === '127.0.0.1' ||
         window.location.hostname.includes('.local');
}

/**
 * Get appropriate cookie settings based on environment
 */
function getCookieSettings(): string {
  if (isLocalhost()) {
    // Localhost settings: no domain restriction, no Secure flag
    return 'path=/; max-age=2592000; SameSite=Lax';
  } else {
    // Production settings: shared .blueticks.co domain with Secure flag
    return 'path=/; domain=.blueticks.co; max-age=2592000; SameSite=Lax; Secure';
  }
}

/**
 * Store attribution data in a shared cookie
 * Multi-touch strategy:
 * - Preserves first_touch (never changes)
 * - Adds new click IDs to conversion_ids arrays
 * Works on both localhost (for testing) and production (.blueticks.co)
 */
export function storeAttributionData(): boolean {
  try {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return false;
    }
    // Attribution cookie and localStorage backup are marketing storage: written only with consent.
    if (!hasMarketingConsent()) {
      return false;
    }

    const utmParams = getUtmParameters();
    const clickIds = getClickIds();
    const clientId = getGaClientId();
    const sessionId = getGaSessionId();
    const landingPage = getLandingPage();
    const existingAttribution = readAttributionData();

    let attributionData: AttributionData;

    if (existingAttribution) {
      // Cookie exists - preserve first_touch, update conversion_ids
      const environment = isLocalhost() ? '[LOCALHOST]' : '[PRODUCTION]';
      console.log(`${environment} Updating existing attribution (preserving first-touch)`);

      // Add new click IDs to arrays (deduplicate)
      const updatedConversionIds = {
        gclids: [...existingAttribution.conversion_ids.gclids],
        fbclids: [...existingAttribution.conversion_ids.fbclids],
        msclkids: [...existingAttribution.conversion_ids.msclkids],
        ttclids: [...existingAttribution.conversion_ids.ttclids],
      };

      // Add new gclid if present and not already in array
      if (clickIds.gclid && !updatedConversionIds.gclids.includes(clickIds.gclid)) {
        updatedConversionIds.gclids.push(clickIds.gclid);
        console.log(`${environment} Added new gclid: ${clickIds.gclid}`);
      }

      // Add new fbclid if present and not already in array
      if (clickIds.fbclid && !updatedConversionIds.fbclids.includes(clickIds.fbclid)) {
        updatedConversionIds.fbclids.push(clickIds.fbclid);
        console.log(`${environment} Added new fbclid: ${clickIds.fbclid}`);
      }

      // Add new msclkid if present and not already in array
      if (clickIds.msclkid && !updatedConversionIds.msclkids.includes(clickIds.msclkid)) {
        updatedConversionIds.msclkids.push(clickIds.msclkid);
        console.log(`${environment} Added new msclkid: ${clickIds.msclkid}`);
      }

      // Add new ttclid if present and not already in array
      if (clickIds.ttclid && !updatedConversionIds.ttclids.includes(clickIds.ttclid)) {
        updatedConversionIds.ttclids.push(clickIds.ttclid);
        console.log(`${environment} Added new ttclid: ${clickIds.ttclid}`);
      }

      attributionData = {
        first_touch: existingAttribution.first_touch, // Never changes
        conversion_ids: updatedConversionIds,
        ga_client_id: clientId || existingAttribution.ga_client_id, // Always update to current if available
        ga_session_id: sessionId || existingAttribution.ga_session_id,
        last_updated: new Date().toISOString(),
      };
    } else {
      // New cookie - initialize with current data
      const environment = isLocalhost() ? '[LOCALHOST]' : '[PRODUCTION]';
      console.log(`${environment} Creating new attribution cookie`);

      attributionData = {
        first_touch: {
          ...utmParams,
          landing_page: landingPage || null,
          timestamp: new Date().toISOString(),
          page_url: window.location.href,
          referrer: document.referrer || '',
        },
        conversion_ids: {
          gclids: clickIds.gclid ? [clickIds.gclid] : [],
          fbclids: clickIds.fbclid ? [clickIds.fbclid] : [],
          msclkids: clickIds.msclkid ? [clickIds.msclkid] : [],
          ttclids: clickIds.ttclid ? [clickIds.ttclid] : [],
        },
        ga_client_id: clientId,
        ga_session_id: sessionId,
        last_updated: new Date().toISOString(),
      };
    }

    // Store cookie with environment-appropriate settings
    const cookieValue = encodeURIComponent(JSON.stringify(attributionData));
    const cookieSettings = getCookieSettings();
    const cookieString = `blueticks_attribution=${cookieValue}; ${cookieSettings}`;

    document.cookie = cookieString;

    // Store backup in localStorage
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('blueticks_attribution_backup', JSON.stringify(attributionData));
      }
    } catch (localStorageError) {
      console.warn('Could not store attribution backup in localStorage:', localStorageError);
    }

    const environment = isLocalhost() ? '[LOCALHOST]' : '[PRODUCTION]';
    console.log(`${environment} Attribution data stored:`, attributionData);
    console.log(`${environment} Cookie settings:`, cookieSettings);

    return true;
  } catch (error) {
    console.warn('Error storing attribution data:', error);
    return false;
  }
}

/**
 * Read attribution data from cookie
 * This function will be used by app.blueticks.co to retrieve the attribution data
 * Falls back to localStorage if cookie is not found
 */
export function readAttributionData(): AttributionData | null {
  try {
    if (typeof document === 'undefined') return null;

    // Try to read from cookie first
    const match = document.cookie.match(/blueticks_attribution=([^;]+)/);
    if (match) {
      const attributionData = JSON.parse(decodeURIComponent(match[1]));
      return attributionData;
    }

    // Fall back to localStorage if cookie not found
    try {
      if (typeof localStorage !== 'undefined') {
        const backupData = localStorage.getItem('blueticks_attribution_backup');
        if (backupData) {
          console.log('Attribution data restored from localStorage backup');
          return JSON.parse(backupData);
        }
      }
    } catch (localStorageError) {
      console.warn('Could not read attribution backup from localStorage:', localStorageError);
    }

    return null;
  } catch (error) {
    console.warn('Error reading attribution data:', error);
    return null;
  }
}

/**
 * Clear attribution cookie (useful for testing or privacy compliance)
 */
export function clearAttributionData(): void {
  try {
    if (typeof document === 'undefined') return;
    
    document.cookie = 'blueticks_attribution=; path=/; domain=.blueticks.co; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    console.log('Attribution data cleared');
  } catch (error) {
    console.warn('Error clearing attribution data:', error);
  }
}

/**
 * Patch ga_client_id and ga_session_id into the existing attribution cookie.
 * Called with the client_id value obtained from gtag('get').
 */
function patchGaIds(clientId: string): void {
  try {
    if (!hasMarketingConsent()) return;
    const data = readAttributionData();
    if (!data) return;

    let changed = false;
    if (!data.ga_client_id) {
      data.ga_client_id = clientId;
      changed = true;
    }

    const sessionId = getGaSessionId();
    if (sessionId && data.ga_session_id !== sessionId) {
      data.ga_session_id = sessionId;
      changed = true;
    }

    if (!changed) return;

    data.last_updated = new Date().toISOString();

    const cookieValue = encodeURIComponent(JSON.stringify(data));
    const cookieSettings = getCookieSettings();
    document.cookie = `blueticks_attribution=${cookieValue}; ${cookieSettings}`;

    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('blueticks_attribution_backup', JSON.stringify(data));
      }
    } catch { /* ignore */ }

    console.log('[ATTRIBUTION] Patched ga_client_id:', clientId, 'ga_session_id:', sessionId);
  } catch (error) {
    console.warn('[ATTRIBUTION] Failed to patch GA IDs:', error);
  }
}

/**
 * Initialize attribution tracking on page load
 * This is the main function to call on the homepage
 */
export function initializeAttribution(): void {
  try {
    // Store UTMs, click IDs, and first-touch immediately (no delay needed)
    storeAttributionData();

    // Use gtag('get') to receive the GA4 client_id via callback.
    // This is event-driven: gtag queues the command and fires the callback
    // exactly when GA4 initializes, regardless of when the script loads.
    if (typeof window !== 'undefined' && (window as any).gtag && hasAnalyticsConsent()) {
      const gaId = (typeof process !== 'undefined' && process.env?.REACT_APP_GA_ID) || 'G-RCEGPEQEZ5';
      (window as any).gtag('get', gaId, 'client_id', (clientId: string) => {
        patchGaIds(clientId);
      });
    }
  } catch (error) {
    console.warn('Attribution tracking initialization failed:', error);
  }
}